import BaseUISystem from '../BaseUISystem';
import BaseMenu from './BaseMenu';
import GameObject, { GameObjectEvent } from '../../../GameObject/GameObject';
import DayItem from '../../../Prototype/UI/DayItem';
import { tVector4 } from '../../../Utility/ArrayHelper';
import Game from '../../../Game';
import UITextComponent from '../../../Component/UI/Text';
import { IGameState } from '../../../State/Game/IGameState';
import DailyLoginTimer from '../../Timer/Timers/DailyLoginTimer';
import {
    dailyLoginPet,
    maxDailyLoginStreak,
    petDayIndex,
} from '../../../Constants/DailyLogin';
import DailyLoginHelper from '../../../Utility/DailyLoginHelper';
import { RewardPools } from '../../../Utility/RewardHelper';
import Overlay from '../../../Prototype/UI/Overlay';
import { PetsStateHelper } from '../../../State/Player/Pets/PetsStateHelper';
import { PlayerActions } from '../../../State/Player/PlayerActions';
import { StartAnimationClip } from '../../..//System/Animation/AnimationHelper';
import { DragonbonesDefault, OverlayFadeIn, OverlayFadeOut, UIPopIn, UIPopOut } from '../../../System/Animation/Clip';
import Element from '../../../Prototype/UI/Element';
import { applyDefaultButtonAnimation } from '../UIHelper';
import { AnimationClipEvent } from '../../../System/Animation/AnimationClip';
import Interactable from '../../../Component/Interactable';
import { PopupID } from '../../../State/UI/PopupID';

const dailyLoginAnchors: tVector4[] = [
    [0.15, 0.4, 0.15, 0.4],
    [0.37, 0.4, 0.37, 0.4],
    [0.59, 0.4, 0.59, 0.4],
    [0.15, 0.73, 0.15, 0.73],
    [0.37, 0.73, 0.37, 0.73],
    [0.59, 0.73, 0.59, 0.73],
    [0.83, 0.56, 0.83, 0.56],
];

export default class DailyLoginMenuHandler extends BaseMenu {
    private timer: DailyLoginTimer;
    private timerTextComponent: UITextComponent;
    private loginStreak: number;
    private readonly dayItems: DayItem[] = [];
    private showPetInDaily!: boolean;

    constructor(uiSystem: BaseUISystem) {
        super(PopupID.DAILY_LOGIN_MENU, uiSystem);
    }

    public onCreate() {
        super.onCreate();

        const timerText = this.element.findChildByName('day_timer');
        this.timerTextComponent = timerText.getComponent(UITextComponent);
        this.cleanupManager.add(() => {
            this.timerTextComponent = null;
        });

        const state = Game.store.getState();
        this.loginStreak = DailyLoginHelper.getCurrentLoginStreakProgress(
            state.playerState
        );

        this.showPetInDaily =
            state.playerState.petsState.petInDailyLogin &&
            !PetsStateHelper.isPetUnlocked(
                state.playerState.petsState,
                dailyLoginPet
            );

        const daysContainer = this.element.find('days');
        for (let i = 0; i < maxDailyLoginStreak; i++) {
            const dayItem = GameObject.Instantiate(DayItem, {
                loginStreak: this.loginStreak,
                showPetInDaily: this.showPetInDaily,
                layout: {
                    anchors: dailyLoginAnchors[i],
                },
                index: i + 1,
            });
            this.dayItems.push(dayItem);
            daysContainer.addChild(dayItem);
        }

        const overlay = GameObject.Instantiate(Overlay, {
            name: 'day_item_overlay',
            anchors: [0.5, 0.5, 0.5, 0.5],
            pivot: [0.5, 0.5],
            scale: [2, 2],
            alpha: 0,
            buttonMode: true,
            enabled: true,
        });
        this.element.addChild(overlay);

        overlay.once(
            GameObjectEvent.SELECTED,
            this.claim
        );

        this.timer = new DailyLoginTimer();
        this.cleanupManager.add(() => {
            this.timer.stop();
        });
    }

    private readonly claim = (overlay: GameObject) => {
        overlay.getComponent(Interactable).enabled = false;
        const dayItem = this.dayItems[this.loginStreak - 1];

        dayItem.onCollectClicked()
            .then(async (rewards) => {
                const rewardToReveal = this.canRevealReward
                    .map((check) => check())
                    .find(((data) => data.canReveal));

                if (rewardToReveal) {
                    await this.revealUpcomingReward(rewardToReveal.dayItem)
                }

                PlayerActions.claimDailyLoginReward(rewards);
            })
    }

    public onShow(params: unknown) {
        const state = Game.store.getState();
        this.timer.onUpdate = this.updateTimerText;
        this.timer.onUpdate(state);
    }
    public onHidden(params: unknown) {
        this.timer.onUpdate = () => {};
    }
    private readonly updateTimerText = (state: IGameState) => {
        if (
            this.timerTextComponent &&
            this.timerTextComponent.container &&
            this.timer?.getDate(state)
        ) {
            this.timerTextComponent.text = this.timer.getFormattedCountdown(
                state
            );
        }
    };
    public async preload() {
        Game.resource.add(
            'atlas-daily-login',
            'resource/graphics/sprite_sheets/dailylogin.json'
        );
        Game.resource.add(
            RewardPools.DAILY_LOGIN,
            'resource/data/rewards/dailyLoginRewards.json'
        );
        super.preload();
    }
    public destroy() {
        this.cleanupManager.cleanUpAll();
        super.destroy();
    }

    private revealUpcomingReward(dayItem: DayItem): Promise<void> {
        return new Promise((resolve) => {
            const daysContainer = this.element.find('days');
            const overlay = GameObject.Instantiate(Overlay, {
                color: [0, 0, 0, 180]
            });

            daysContainer.addChild(overlay);
            daysContainer.addChild(dayItem);

            const uiElements = GameObject.Instantiate(Element,
                    {
                        name: 'continue_button_bg',
                        layout: {
                            anchors: [0.5, 1, 0.5, 1],
                            pivot: [0.5, 0],
                            offsets: [0, -100, 0, -100],
                            scale: [0, 0],
                        },
                        components: [
                            {
                                type: 'image',
                                data: {
                                    cacheKey: 'blank',
                                },
                            },
                        ],
                        children: [
                            {
                                name: 'continue_button',
                                layout: {
                                    anchors: [0.5, 0.5, 0.5, 0.5],
                                    pivot: [0.5, 0.5],
                                    offsets: [0, -30, 0, -30],
                                    scale: [0.85, 0.85],
                                },
                                components: [
                                    {
                                        type: 'image',
                                        data: {
                                            cacheKey: 'button_medium_green',
                                        },
                                    },
                                    {
                                        type: 'input',
                                        data: {
                                            buttonMode: true,
                                            enabled: true,
                                        },
                                    },
                                ],
                                children: [
                                    {
                                        name: 'text',
                                        layout: {
                                            anchors: [0.5, 0.5, 0.5, 0.5],
                                            pivot: [0.5, 0.5],
                                            offsets: [0, 3, 0, 3],
                                            scale: [1, 1],
                                        },
                                        components: [
                                            {
                                                type: 'text',
                                                data: {
                                                    textLocalizationKey: 'Continue',
                                                    style: 'pet.unlock.continue',
                                                    bounds: [230, 100],
                                                },
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                name: 'text',
                                layout: {
                                    anchors: [0.5, 0, 0.5, 0],
                                    pivot: [0.5, 0.5],
                                    offsets: [0, 60, 0, 60],
                                },
                                components: [
                                    {
                                        type: 'text',
                                        data: {
                                            textLocalizationKey: 'PetRevealReward',
                                            style: 'pet.unlock.unlockTomorrow',
                                            bounds: [640, 150]
                                        },
                                    },
                                    {
                                        type: 'visibility',
                                        data: {
                                            visible: false,
                                        },
                                    },
                                ],
                            }
                        ],
            });

            const button = uiElements.find('continue_button');
            applyDefaultButtonAnimation(button);
            button.once(GameObjectEvent.SELECTED, () => {
                complete();
            });

            dayItem.addChild(uiElements);

            StartAnimationClip(new OverlayFadeIn(overlay));
            StartAnimationClip(new UIPopIn(uiElements));

            // Open pet box
            StartAnimationClip(new DragonbonesDefault(dayItem.giftPetArmature, {
                animationName: "opening",
                playTimes: 1
            }).once(AnimationClipEvent.DESTROY, () => {
                StartAnimationClip(new DragonbonesDefault(dayItem.giftPetArmature, {
                    animationName: "open_idle"
                }));
            }))

            return;
            function complete() {
                StartAnimationClip(
                    new OverlayFadeOut(overlay).once(
                        AnimationClipEvent.DESTROY,
                        () => {
                            overlay?.destroy();
                        }
                    )
                );
                StartAnimationClip(
                    new UIPopOut(uiElements).once(
                        AnimationClipEvent.DESTROY,
                        () => {
                            uiElements?.destroy();
                        }
                    )
                );
                resolve();
            }
        })

    }

    private canRevealReward: (() => IDayItemRevealValidationData)[] = [
        // Check if pet reward can be revealed
        () => {
            return {
                dayItem: this.dayItems[petDayIndex - 1],
                canReveal: this.showPetInDaily && this.loginStreak === petDayIndex - 1
            }
        }
    ]
}

interface IDayItemRevealValidationData {
    canReveal: boolean,
    dayItem: DayItem
}
